/**
 * VÉRAULT — ProposalItemCard
 *
 * Editorial card rendering for an Experience Item inside the published
 * client-facing proposal. Replaces the previous plain-text Surface card
 * that flattened everything into a single paragraph.
 *
 * Behaviour rules (Feb 2026 QA pass):
 *   · Eyebrow shows the readable category label (Activity, Event, …).
 *   · Title and location render once, scannable.
 *   · `client_copy` is rendered as markdown so paragraph breaks, bullets,
 *     bold/italic, and dividers from the editor carry through to the
 *     published proposal.
 *   · Structured detail rows render only when their values exist — no
 *     empty labels, no raw field names.
 *   · `advisor_notes`, `operational_notes`, `supplier_name`, and any
 *     cost field are NEVER rendered client-side.
 *   · Each card stays linked to its source item via data-item-id for
 *     deep-link / PeekDrawer wiring later.
 */
import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Surface } from "../../../index";
import { pexelsifyUrl } from "../../../../utils/pexels";

// ── Category label map ────────────────────────────────────────────
const CATEGORY_LABEL = {
  hotel: "Hotel",
  event: "Event",
  activity: "Activity",
  experience: "Experience",
  restaurant: "Dining",
  dining: "Dining",
  transfer: "Transfer",
  flight: "Flight",
  logistics: "Logistics",
  car_service: "Transfer",
  car_rental: "Car",
};

function readableCategory(category) {
  if (!category) return null;
  const k = String(category).toLowerCase();
  return CATEGORY_LABEL[k] || category.replace(/_/g, " ");
}

// ── Markdown renderer (calm editorial styling) ────────────────────
const MD_COMPONENTS = {
  p: ({ node, ...props }) => (
    <p className="font-serif text-[14.5px] sm:text-[15px] leading-[1.75] text-[hsl(var(--foreground-secondary))] my-2 first:mt-0 last:mb-0" {...props} />
  ),
  strong: ({ node, ...props }) => <strong className="font-medium text-[hsl(var(--foreground))]" {...props} />,
  em: ({ node, ...props }) => <em className="font-accent italic text-[hsl(var(--foreground-secondary))]" {...props} />,
  ul: ({ node, ...props }) => <ul className="list-disc pl-5 my-2 space-y-0.5 font-serif text-[14.5px] text-[hsl(var(--foreground-secondary))]" {...props} />,
  ol: ({ node, ...props }) => <ol className="list-decimal pl-5 my-2 space-y-0.5 font-serif text-[14.5px] text-[hsl(var(--foreground-secondary))]" {...props} />,
  li: ({ node, ...props }) => <li className="leading-relaxed" {...props} />,
  blockquote: ({ node, ...props }) => (
    <blockquote className="border-l-2 border-[hsl(var(--accent-gold))]/40 pl-3 my-3 font-accent italic text-[14px] text-[hsl(var(--foreground-muted))]" {...props} />
  ),
  hr: () => <div className="my-3 h-px bg-[hsl(var(--surface-hairline))]" />,
  a: ({ node, ...props }) => (
    <a className="underline decoration-[hsl(var(--accent-gold))]/60 underline-offset-2 hover:text-[hsl(var(--accent-gold))]" target="_blank" rel="noopener noreferrer" {...props} />
  ),
};

// ── Time formatting ───────────────────────────────────────────────
function formatTiming(item) {
  if (item.timing_label) return item.timing_label;
  if (item.start_at) {
    try {
      const d = new Date(item.start_at);
      return d.toLocaleString(undefined, {
        weekday: "long",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    } catch {
      return item.start_at;
    }
  }
  if (item.day_label) return item.day_label;
  return null;
}

function formatLocation(item) {
  const bits = [];
  if (item.city) bits.push(item.city);
  else if (item.destination) bits.push(item.destination);
  if (item.country) bits.push(item.country);
  return bits.join(" · ") || null;
}

// ── Detail row component ──────────────────────────────────────────
function DetailRow({ label, value, testid }) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <div className="flex items-baseline gap-3 py-1" data-testid={testid}>
      <span className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] shrink-0 w-[120px]">
        {label}
      </span>
      <span className="font-serif text-[14px] text-[hsl(var(--foreground-secondary))] leading-relaxed flex-1 min-w-0">
        {value}
      </span>
    </div>
  );
}

// ── Main card ─────────────────────────────────────────────────────
export default function ProposalItemCard({ item }) {
  if (!item) return null;
  const eyebrow = readableCategory(item.category);
  const location = formatLocation(item);
  const timing = formatTiming(item);

  // Optional structured fields (read top-level OR nested `details` for
  // forward-compat with the new editor schema).
  const details = item.details || {};
  const bestFor = item.best_for || details.best_for;
  const pace = item.pace || details.pace;
  const designNote = item.design_note || details.design_note;
  const clientNotes = item.client_notes || details.client_notes || item.notes_client;
  const included = item.included || details.included;
  const notIncluded = item.not_included || details.not_included;
  const watchFor = item.watch_for || details.watch_for;
  const dressCode = item.dress_code || details.dress_code;

  // Cleanly distinguish "has rich body" vs "title only"
  const hasBody = Boolean(item.client_copy);

  const sourceUrl = item.official_website || item.url || null;

  return (
    <Surface
      variant="paper"
      padded
      className="!p-5 sm:!p-6 space-y-3"
      data-testid={`client-proposal-item-${item.id}`}
      data-item-id={item.id}
    >
      {/* Image (if present) */}
      {item.image_url && (
        <div className="-m-5 sm:-m-6 mb-3 sm:mb-4 overflow-hidden">
          <img
            src={pexelsifyUrl(item.image_url)}
            alt=""
            className="w-full h-44 sm:h-56 object-cover"
            data-testid={`client-proposal-item-${item.id}-image`}
            onError={(e) => { e.target.style.display = "none"; }}
          />
        </div>
      )}

      {/* Eyebrow */}
      {eyebrow && (
        <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--accent-gold))]">
          {eyebrow}
        </p>
      )}

      {/* Title */}
      <h3 className="font-serif text-[18px] sm:text-[20px] leading-tight text-[hsl(var(--foreground))]">
        {item.title}
      </h3>

      {/* Location */}
      {location && (
        <p className="font-accent italic text-[13px] text-[hsl(var(--foreground-muted))] -mt-1">
          {location}
        </p>
      )}

      {/* Short copy / editorial body — markdown so line breaks + bullets render */}
      {hasBody && (
        <div className="pt-1 proposal-item-body">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
            {item.client_copy}
          </ReactMarkdown>
        </div>
      )}

      {/* Detail rows — only render when at least one field present */}
      {(timing || bestFor || pace || designNote || clientNotes || included || notIncluded || watchFor || dressCode || item.address || item.role_in_trip || item.why_fits) && (
        <div className="pt-2 mt-1 border-t border-[hsl(var(--surface-hairline))] space-y-0.5">
          <DetailRow label="Timing" value={timing} testid="proposal-item-row-timing" />
          <DetailRow label="Best for" value={bestFor} testid="proposal-item-row-best-for" />
          <DetailRow label="Pace" value={pace} testid="proposal-item-row-pace" />
          <DetailRow label="Why this fits" value={item.why_fits} testid="proposal-item-row-why-fits" />
          <DetailRow label="Role" value={item.role_in_trip} testid="proposal-item-row-role" />
          <DetailRow label="Address" value={item.address} testid="proposal-item-row-address" />
          <DetailRow label="Dress" value={dressCode} testid="proposal-item-row-dress" />
          <DetailRow label="Included" value={included} testid="proposal-item-row-included" />
          <DetailRow label="Not included" value={notIncluded} testid="proposal-item-row-not-included" />
          <DetailRow label="Worth watching" value={watchFor} testid="proposal-item-row-watch-for" />
          <DetailRow label="A note" value={designNote} testid="proposal-item-row-design-note" />
          {clientNotes && (
            <DetailRow label="Notes" value={clientNotes} testid="proposal-item-row-client-notes" />
          )}
        </div>
      )}

      {/* Optional source / detail link */}
      {sourceUrl && (
        <div className="pt-2">
          <a
            href={sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))] hover:underline"
            data-testid={`client-proposal-item-${item.id}-source`}
          >
            View details ↗
          </a>
        </div>
      )}
    </Surface>
  );
}
